import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Course, ExternalCourse } from 'graphqlAPI'
import { formatThousandsSeparator } from 'helpers/others.helper'
import {
  createRouteMonitoringCourse,
  createRouteMonitoringExternalCourse,
} from 'enum/routes'
import { ExternalCourseIndicator } from 'components/ui/CourseCards/components/molecules/externalCourseIndicator'
import { prefix } from '../../constants'
import { ProgressEvaluation } from '../../components'
import Options from '../Options'
import Progress from '../CourseProgress'
import EvaluationProgressPopover from '../../../../EvaluationProgressPopover'
import tableStyles from '../../index.module.scss'
import styles from './useColumns.module.scss'

const ACTION_COLUMN_WIDTH = 45
const NAME_COLUMN_WIDTH = 300

type Data = Course | ExternalCourse
export function useColumns() {
  const { t, i18n } = useTranslation()
  const withFormattedCount = useCallback(
    (translationKey: string, count: number) =>
      t(translationKey, { count }).replace(
        String(count),
        formatThousandsSeparator(count, i18n.language)
      ),
    [i18n.language, t]
  )
  type Column = {
    title: string
    key:
      | keyof Data
      | 'advance_finish'
      | 'trim_name'
      | 'progress_evaluations_approved'
      | 'action'
    sorteable?: boolean
    width?: number
    hideable?: boolean
    align?: 'left' | 'center' | 'right'
    fixed?: 'left' | 'right'
    className?: string
    render?: (...data: any) => any
  }

  const prefixHeaders = `${prefix}.headers`
  const prefixColumns = `${prefix}.columns`

  const columns: Column[] = useMemo(
    () => [
      {
        title: t(`${prefixHeaders}.nameCourse`),
        key: 'trim_name',
        sorteable: true,
        hideable: false,
        width: NAME_COLUMN_WIDTH,
        fixed: 'left',
        className: tableStyles.nameColumn,
        render: (_, { id, name, __typename, integrationCourseLink }: any) => {
          const isExternalCourse = __typename === 'ExternalCourse'
          return (
            <div className={styles.name}>
              <Link
                to={
                  isExternalCourse
                    ? createRouteMonitoringExternalCourse({ id: `${id}` })
                    : createRouteMonitoringCourse({ id: `${id}` })
                }
                className={styles.link}
              >
                {name}
              </Link>
              <ExternalCourseIndicator
                origin="TABLE"
                url={integrationCourseLink}
              />
            </div>
          )
        },
      },
      {
        title: t(`${prefixHeaders}.type`),
        key: '__typename',
        width: 150,
        sorteable: true,
        render: (value) => <span>{t(`${prefixColumns}.types.${value}`)}</span>,
      },
      {
        title: t(`${prefixHeaders}.enrolments`, {
          defaultValue: 'Inscripciones',
        }),
        key: 'participantsCount',
        width: 170,
        sorteable: true,
        render: (value) => (
          <span>
            {withFormattedCount(`${prefixColumns}.participants`, value ?? 0)}
          </span>
        ),
      },
      {
        title: t(`${prefixHeaders}.progress`),
        key: 'advance_finish',
        width: 120,
        sorteable: true,
        render: (_, value?: Data) =>
          (value?.progress && (
            <Progress id={'progress-' + value.id} progress={value.progress} />
          )) ||
          '---',
      },
      {
        title: t(`${prefixHeaders}.progressEvaluations`),
        key: 'progress_evaluations_approved',
        width: 120,
        sorteable: true,
        render: (_, value?: Data) => {
          const progress = value?.progressEvaluations
          const participantsCount = value?.participantsCount
          return (
            (progress?.total && participantsCount && (
              <EvaluationProgressPopover
                progress={{
                  approved: progress.approved,
                  reproved: progress.reproved,
                }}
                total={participantsCount * progress.total}
              >
                <ProgressEvaluation
                  progress={progress}
                  total={participantsCount * progress.total}
                  text={(percentages) => (
                    <>
                      {t(`${prefixColumns}.progressEvaluation`, {
                        count: percentages.approved,
                      })}
                    </>
                  )}
                />
              </EvaluationProgressPopover>
            )) || <div className="text-center">---</div>
          )
        },
      },
      {
        title: t(`${prefixHeaders}.hours`),
        key: 'hours',
        width: 140,
        sorteable: true,
        render: (data) => (
          <span>
            {t(`${prefixColumns}.hoursTotal`, {
              count: +(data || 0).toFixed(2),
            })}
          </span>
        ),
      },
      {
        title: '',
        key: 'action',
        width: ACTION_COLUMN_WIDTH,
        hideable: false,
        align: 'center',
        fixed: 'right',
        className: tableStyles.actionColumn,
        render: (id, course) => <Options course={course} />,
      },
    ],
    [i18n.language, prefixColumns, prefixHeaders, t, withFormattedCount]
  )
  return columns
}
